import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, X } from "lucide-react";

/**
 * PWA install prompt.
 * - On Chrome/Edge (Android + desktop): captures `beforeinstallprompt` and shows a native install prompt.
 * - On iOS Safari: shows a hint bubble ("Tap Share → Add to Home Screen").
 * - Once installed (standalone display), the prompt hides itself.
 */
export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("carmel_install_dismissed") === "1");

  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);

  useEffect(() => {
    if (isStandalone || dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show iOS instructions after a short delay if user is on iOS
    if (isIOS) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [dismissed, isIOS, isStandalone]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem("carmel_install_dismissed", "1");
    setDismissed(true);
    setVisible(false);
  };

  if (!visible || isStandalone || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50 bg-carmel-navy text-white rounded-lg shadow-elevated p-4 animate-fade-in-up"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-white/60 hover:text-white transition-colors"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-carmel-gold/20 text-carmel-goldLight flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="font-serif text-lg tracking-tight leading-tight">Install Carmel SSS</p>
          <p className="text-xs text-white/70 mt-1">
            {isIOS
              ? "Tap the Share icon in Safari, then choose \"Add to Home Screen\" — the app will open fullscreen like a native app."
              : "Add the app to your phone home screen for one-tap access, offline support, and a fullscreen experience."}
          </p>
          {!isIOS && deferred && (
            <Button
              onClick={install}
              size="sm"
              className="mt-3 bg-carmel-gold hover:bg-carmel-goldDark text-white"
              data-testid="pwa-install-button"
            >
              Install on this device
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
